import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { Theme, ThemeContextType, ThemeColors } from '../types';


const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const lightColors: ThemeColors = {
  primary: '#BD8C46',
  primaryHover: '#A67A3D',
  primaryLight: '#E8D4B8',
  background: '#FEFCF8',
  surface: '#FFFFFF',
  text: '#1F2937',
  textSecondary: '#6B7280',
  border: '#9CA3AF',
  success: '#047857',
  warning: '#B45309',
  error: '#B91C1C',
};

const darkColors: ThemeColors = {
  primary: '#D4A866',
  primaryHover: '#E8D4B8',
  primaryLight: '#BD8C46',
  background: '#111827',
  surface: '#1F2937',
  text: '#F9FAFB',
  textSecondary: '#D1D5DB',
  border: '#374151',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>('light');

  useEffect(() => {
    try {
      // Load theme preference from the same localStorage key as PreferencesProvider
      const savedPreferences = localStorage.getItem('holiday-dashboard-preferences');
      let initialTheme: Theme = 'light';
      
      if (savedPreferences) {
        const parsed = JSON.parse(savedPreferences);
        initialTheme = parsed.theme || 'light';
      }
      
      setThemeState(initialTheme);
      applyTheme(initialTheme);
    } catch (error) {
      console.warn('Failed to load theme preference:', error);
      // Fallback to light theme on any error
      const fallbackTheme: Theme = 'light';
      setThemeState(fallbackTheme);
      applyTheme(fallbackTheme);
    }
  }, []);

  const applyTheme = (newTheme: Theme) => {
    const colors = newTheme === 'light' ? lightColors : darkColors;
    const root = document.documentElement;
    
    root.setAttribute('data-theme', newTheme);
    
    // Apply CSS custom properties for backward compatibility
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`, value);
    });
  };

  const setTheme = (newTheme: Theme) => {
    try {
      setThemeState(newTheme);
      
      // Update the preferences in localStorage to keep them in sync
      const savedPreferences = localStorage.getItem('holiday-dashboard-preferences');
      let preferences = { theme: 'light', language: 'ar' };
      
      if (savedPreferences) {
        preferences = JSON.parse(savedPreferences);
      }
      
      preferences.theme = newTheme;
      localStorage.setItem('holiday-dashboard-preferences', JSON.stringify(preferences));
      
      applyTheme(newTheme);
    } catch (error) {
      console.warn('Failed to save theme preference:', error);
      // Still apply the theme even if saving fails
      applyTheme(newTheme);
    }
  };

  const colors = theme === 'light' ? lightColors : darkColors;

  return (
    <ThemeContext.Provider value={{ theme, setTheme, colors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};